import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, ReplaySubject, shareReplay } from 'rxjs';
import { tap, filter, take, switchMap, map } from 'rxjs/operators';
import { ApiServiceBase } from './api.service.base';
import {
  UserStateResponse,
  UserInfo,
  OriginalUserPermissions,
  ImpersonationState,
  ClientInfo,
} from '../../models/user-state.model';

import { logger } from '../../helpers/logger';
@Injectable({
  providedIn: 'root',
})
export class UserStateService extends ApiServiceBase {
  private userStateSubject = new BehaviorSubject<UserStateResponse | null>(null);
  private stateInitialized$ = new ReplaySubject<boolean>(1);
  private loadingState = false;

  public readonly userState$ = this.userStateSubject.asObservable();
  public readonly stateInitialized = this.stateInitialized$.asObservable();

  // Derived observables for specific parts of the state
  public readonly isAuthenticated$ = this.userState$.pipe(map(state => state?.isAuthenticated ?? false));

  public readonly currentUser$ = this.userState$.pipe(map(state => state?.user ?? null));

  public readonly originalUserPermissions$ = this.userState$.pipe(map(state => state?.originalUserPermissions ?? null));

  public readonly impersonationState$ = this.userState$.pipe(map(state => state?.impersonationState ?? null));

  public readonly currentClient$ = this.userState$.pipe(map(state => state?.currentClient ?? null));

  constructor() {
    super();
    this.loadUserState();
  }

  /**
   * Load user state from the consolidated endpoint (single call, shared across all subscribers)
   */
  private loadUserState(): void {
    if (this.loadingState) {
      return; // Prevent multiple simultaneous calls
    }

    this.loadingState = true;

    this.get<UserStateResponse>('account/user-state')
      .pipe(
        shareReplay(1) // Share the result with all subscribers
      )
      .subscribe({
        next: userState => {
          this.userStateSubject.next(userState);
          this.stateInitialized$.next(true);
          this.loadingState = false;
        },
        error: error => {
          // Only log errors that aren't 401 Unauthorized (expected for unauthenticated users)
          if (error.status !== 401) {
            logger.error('Failed to load user state:', error);
          }
          // Set unauthenticated state
          this.userStateSubject.next({ isAuthenticated: false });
          this.stateInitialized$.next(true);
          this.loadingState = false;
        },
      });
  }

  /**
   * Refresh user state (call after login, logout, etc.)
   */
  public refreshUserState(): void {
    this.loadingState = false; // Reset loading state to allow refresh
    this.loadUserState();
  }

  /**
   * Get current user state
   */
  public getCurrentUserState(): UserStateResponse | null {
    return this.userStateSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    const state = this.userStateSubject.value;
    return state?.isAuthenticated ?? false;
  }

  /**
   * Get current user info
   */
  public getCurrentUser(): UserInfo | null {
    const state = this.userStateSubject.value;
    return state?.user ?? null;
  }

  /**
   * Check if user is admin
   */
  public isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin ?? false;
  }

  /**
   * Get user name
   */
  public getUserName(): string {
    const user = this.getCurrentUser();
    return user?.userName ?? '';
  }

  /**
   * Get user email
   */
  public getUserEmail(): string {
    const user = this.getCurrentUser();
    return user?.email ?? '';
  }

  /**
   * Check if user is showcase user
   */
  public isShowcaseUser(): boolean {
    const user = this.getCurrentUser();
    return user?.isShowcaseUser ?? false;
  }

  /**
   * Get original user permissions
   */
  public getOriginalUserPermissions(): OriginalUserPermissions | null {
    const state = this.userStateSubject.value;
    return state?.originalUserPermissions ?? null;
  }

  /**
   * Get impersonation state
   */
  public getImpersonationState(): ImpersonationState | null {
    const state = this.userStateSubject.value;
    return state?.impersonationState ?? null;
  }

  /**
   * Check if user is impersonating
   */
  public isImpersonating(): boolean {
    const impersonationState = this.getImpersonationState();
    return impersonationState?.isImpersonating ?? false;
  }

  /**
   * Get current client info
   */
  public getCurrentClient(): ClientInfo | null {
    const state = this.userStateSubject.value;
    return state?.currentClient ?? null;
  }

  /**
   * Get user claims
   */
  public getUserClaims(): string[] {
    const state = this.userStateSubject.value;
    return state?.userClaims ?? [];
  }

  /**
   * Check if user has a specific claim
   */
  public hasClaim(claim: string): boolean {
    if (this.isAdmin()) {
      return true;
    }

    const claims = this.getUserClaims();
    return claims.includes(claim);
  }

  /**
   * Check if user can create projects
   */
  public canCreateProjects(): boolean {
    return this.hasClaim('Engage.Project.Create');
  }

  /**
   * Returns an observable that emits the authentication status only after initialization is complete
   */
  public getAuthenticationStatus(): Observable<boolean> {
    return this.stateInitialized$.pipe(
      filter(initialized => initialized),
      take(1),
      switchMap(() => this.userState$),
      take(1),
      tap(() => {}), // Ensure the state check has completed
      switchMap(state => [state?.isAuthenticated ?? false])
    );
  }

  /**
   * Wait for state to be initialized and return the current state
   */
  public getInitializedUserState(): Observable<UserStateResponse> {
    return this.stateInitialized$.pipe(
      filter(initialized => initialized),
      take(1),
      switchMap(() => this.userState$),
      filter(state => state !== null),
      take(1)
    ) as Observable<UserStateResponse>;
  }
}
